import { useEffect, useReducer } from 'react';
import { getApiErrorMessage } from '@/shared/lib/getApiErrorMessage';
import { Modal } from '../../atoms/Modal';
import { ModalFooter } from '../../molecules/ModalFooter';
import { MarkdownViewer } from '../../molecules/MarkdownEditor';
import * as s from './QuestAcceptModal.css';

export interface QuestRewardPreview {
  type: 'coin' | 'exp' | 'item';
  amount: number;
  label: string;
}

export interface QuestAcceptModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  questTitle: string;
  questDescription?: string;
  questRarity?: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  rewards?: QuestRewardPreview[];
  autoCloseDelay?: number;
}

type AcceptPhase = 'confirm' | 'submitting' | 'accepted' | 'error';
type AcceptState = { phase: Exclude<AcceptPhase, 'error'> } | { phase: 'error'; message: string };
type AcceptAction =
  { type: 'submit' } | { type: 'success' } | { type: 'error'; message: string } | { type: 'reset' };

function acceptReducer(_state: AcceptState, action: AcceptAction): AcceptState {
  switch (action.type) {
    case 'submit':
      return { phase: 'submitting' };
    case 'success':
      return { phase: 'accepted' };
    case 'error':
      return { phase: 'error', message: action.message };
    case 'reset':
      return { phase: 'confirm' };
  }
}

export function QuestAcceptModal({
  open,
  onClose,
  onConfirm,
  questTitle,
  questDescription,
  rewards = [],
  autoCloseDelay = 2500,
}: QuestAcceptModalProps) {
  const [state, dispatch] = useReducer(acceptReducer, { phase: 'confirm' });
  const { phase } = state;

  useEffect(() => {
    if (!open) dispatch({ type: 'reset' });
  }, [open]);

  useEffect(() => {
    if (phase !== 'accepted' || autoCloseDelay <= 0) return;
    const timer = setTimeout(onClose, autoCloseDelay);
    return () => clearTimeout(timer);
  }, [phase, autoCloseDelay, onClose]);

  // onConfirm 을 async 로 대우 — 성공 시에만 accepted 전이(+자동 닫힘), 실패 시 error 상태를
  // 표시하고 모달을 열어 둬 재시도/닫기가 가능. mutation 실패를 성공으로 오인하던 결함 수정.
  const handleConfirm = async () => {
    dispatch({ type: 'submit' });
    try {
      await Promise.resolve(onConfirm());
      dispatch({ type: 'success' });
    } catch (error) {
      dispatch({
        type: 'error',
        message: getApiErrorMessage(error, '퀘스트 수락에 실패했습니다. 다시 시도해주세요.'),
      });
    }
  };

  const totalReward = rewards
    .filter((r) => r.type === 'coin')
    .reduce((sum, r) => sum + r.amount, 0);

  return phase === 'accepted' ? (
    <AcceptedView open={open} onClose={onClose} questTitle={questTitle} totalReward={totalReward} />
  ) : (
    <ConfirmView
      open={open}
      onClose={onClose}
      onConfirm={() => {
        void handleConfirm();
      }}
      questTitle={questTitle}
      questDescription={questDescription}
      rewards={rewards}
      loading={phase === 'submitting'}
      errorMessage={state.phase === 'error' ? state.message : undefined}
    />
  );
}

interface ConfirmViewProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  questTitle: string;
  questDescription?: string;
  rewards: QuestRewardPreview[];
  loading: boolean;
  errorMessage?: string;
}

function ConfirmView({
  open,
  onClose,
  onConfirm,
  questTitle,
  questDescription,
  rewards,
  loading,
  errorMessage,
}: ConfirmViewProps) {
  const hasError = errorMessage !== undefined;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="퀘스트 수락"
      footer={
        <ModalFooter
          primaryLabel={hasError ? '다시 시도' : '수락하기'}
          onPrimaryClick={onConfirm}
          onSecondaryClick={onClose}
          loading={loading}
        />
      }
    >
      <div className={s.content}>
        <div className={s.questInfoCard}>
          <div className={s.questTitle}>{questTitle}</div>
          {questDescription && (
            <div className={s.questDescription}>
              <MarkdownViewer value={questDescription} />
            </div>
          )}
        </div>

        {rewards.length > 0 && (
          <div className={s.rewardPreview}>
            {rewards.map((reward) => (
              <div key={`${reward.type}-${reward.label}`} className={s.rewardItem}>
                <span className={s.rewardAmount}>+{reward.amount.toLocaleString()}</span>
                <span className={s.rewardLabel}>{reward.label}</span>
              </div>
            ))}
          </div>
        )}

        {hasError ? (
          <p className={s.errorMessage} role="alert">
            {errorMessage}
          </p>
        ) : (
          <div className={s.confirmMessage}>
            이 퀘스트를 수락하시겠습니까? 수락하면 바로 시작됩니다.
          </div>
        )}
      </div>
    </Modal>
  );
}

interface AcceptedViewProps {
  open: boolean;
  onClose: () => void;
  questTitle: string;
  totalReward: number;
}

function AcceptedView({ open, onClose, questTitle, totalReward }: AcceptedViewProps) {
  return (
    <Modal open={open} onClose={onClose} title="퀘스트 수락 완료!" titleHidden>
      <div className={s.acceptedContainer}>
        <div className={s.successIconWrapper}>
          <svg
            className={s.successIcon}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              className={s.checkPath}
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        {/* 같은 문구가 Modal 의 숨김 제목으로 이미 접근성 트리에 있다 — 중복 낭독 방지. */}
        <div className={s.acceptedTitle} aria-hidden>
          퀘스트 수락 완료!
        </div>
        <div className={s.acceptedMessage}>
          "{questTitle}" 퀘스트가 시작되었습니다.
          <br />
          행운을 빕니다!
        </div>
        {totalReward > 0 && (
          <div className={s.rewardBanner}>
            <span className={s.rewardBannerText}>
              🏆 완료 시 {totalReward.toLocaleString()}P 획득
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}
